import { useEffect, useMemo, useState } from 'react';
import { FileDown, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';

type JobOption = {
  id: string;
  title: string;
};

type ContractOption = {
  id: string;
  title: string;
  job: { title: string };
  invitation?: { kolProfile?: { name?: string | null } | null } | null;
};

type KolOption = {
  id: string;
  name: string;
};

function slugifyName(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

const nativeOptionStyle = { backgroundColor: 'Canvas', color: 'CanvasText' } as const;

export default function BrandReports() {
  const [jobs, setJobs] = useState<JobOption[]>([]);
  const [contracts, setContracts] = useState<ContractOption[]>([]);
  const [kols, setKols] = useState<KolOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedShortlistJobId, setSelectedShortlistJobId] = useState('');
  const [selectedContractId, setSelectedContractId] = useState('');
  const [selectedKolId, setSelectedKolId] = useState('');
  const [activeExport, setActiveExport] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [jobsRes, contractsRes, kolsRes] = await Promise.all([
          api.get('/jobs'),
          api.get('/jobs/contracts-overview'),
          api.get('/kols/brand-visible'),
        ]);
        if (!active) return;
        const jobRows = (jobsRes.data || []) as JobOption[];
        const contractRows = (contractsRes.data || []) as ContractOption[];
        const kolRows = (kolsRes.data || []) as KolOption[];
        setJobs(jobRows);
        setContracts(contractRows);
        setKols(kolRows);
        setSelectedJobId(jobRows[0]?.id || '');
        setSelectedShortlistJobId(jobRows[0]?.id || '');
        setSelectedContractId(contractRows[0]?.id || '');
        setSelectedKolId(kolRows[0]?.id || '');
      } catch (err: any) {
        if (!active) return;
        setError(err.response?.data?.error || 'Failed to load report data');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const selectedJob = useMemo(() => jobs.find((j) => j.id === selectedJobId) || null, [jobs, selectedJobId]);
  const selectedShortlistJob = useMemo(
    () => jobs.find((j) => j.id === selectedShortlistJobId) || null,
    [jobs, selectedShortlistJobId]
  );
  const selectedContract = useMemo(
    () => contracts.find((c) => c.id === selectedContractId) || null,
    [contracts, selectedContractId]
  );
  const selectedKol = useMemo(() => kols.find((k) => k.id === selectedKolId) || null, [kols, selectedKolId]);

  const download = async (key: string, url: string, filename: string, params?: Record<string, string>) => {
    try {
      setActiveExport(key);
      const res = await api.get(url, { responseType: 'blob', params });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error(err);
      alert('Failed to export PDF');
    } finally {
      setActiveExport(null);
    }
  };

  if (loading) {
    return <div className="py-10 text-glass-text-3">Loading reports...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">Reports</h1>
        <p className="text-sm text-glass-text-2 mt-1">Generate every brand-accessible PDF report from one place.</p>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-500 dark:text-rose-300">
          {error}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Campaign summary PDF</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <select
              value={selectedJobId}
              onChange={(e) => setSelectedJobId(e.target.value)}
              className="w-full rounded-lg border border-glass bg-glass-2 px-3 py-2 text-sm text-glass-text"
            >
              {jobs.map((j) => (
                <option key={j.id} value={j.id} style={nativeOptionStyle}>
                  {j.title}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              disabled={!selectedJob || activeExport === 'campaign-summary'}
              onClick={() =>
                selectedJob &&
                download(
                  'campaign-summary',
                  `/jobs/${encodeURIComponent(selectedJob.id)}/campaign-summary.pdf`,
                  `campaign-${slugifyName(selectedJob.title || selectedJob.id)}.pdf`,
                  { brandTitle: selectedJob.title }
                )
              }
            >
              {activeExport === 'campaign-summary' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              Export PDF
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Shortlist PDF</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <select
              value={selectedShortlistJobId}
              onChange={(e) => setSelectedShortlistJobId(e.target.value)}
              className="w-full rounded-lg border border-glass bg-glass-2 px-3 py-2 text-sm text-glass-text"
            >
              {jobs.map((j) => (
                <option key={j.id} value={j.id} style={nativeOptionStyle}>
                  {j.title}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              disabled={!selectedShortlistJob || activeExport === 'shortlist'}
              onClick={() =>
                selectedShortlistJob &&
                download(
                  'shortlist',
                  `/invitations/jobs/${encodeURIComponent(selectedShortlistJob.id)}/shortlist.pdf`,
                  `shortlist-${slugifyName(selectedShortlistJob.title || selectedShortlistJob.id)}.pdf`
                )
              }
            >
              {activeExport === 'shortlist' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              Export PDF
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contract PDF</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <select
              value={selectedContractId}
              onChange={(e) => setSelectedContractId(e.target.value)}
              className="w-full rounded-lg border border-glass bg-glass-2 px-3 py-2 text-sm text-glass-text"
            >
              {contracts.map((c) => (
                <option key={c.id} value={c.id} style={nativeOptionStyle}>
                  {c.job.title} {c.invitation?.kolProfile?.name ? `- ${c.invitation.kolProfile.name}` : ''}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              disabled={!selectedContract || activeExport === 'contract'}
              onClick={() =>
                selectedContract &&
                download(
                  'contract',
                  `/contracts/${encodeURIComponent(selectedContract.id)}/pdf`,
                  `contract-${selectedContract.id.slice(0, 8)}.pdf`
                )
              }
            >
              {activeExport === 'contract' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              Export PDF
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Rate card PDF</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <select
              value={selectedKolId}
              onChange={(e) => setSelectedKolId(e.target.value)}
              className="w-full rounded-lg border border-glass bg-glass-2 px-3 py-2 text-sm text-glass-text"
            >
              {kols.map((k) => (
                <option key={k.id} value={k.id} style={nativeOptionStyle}>
                  {k.name}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              disabled={!selectedKol || activeExport === 'rate-card'}
              onClick={() =>
                selectedKol &&
                download(
                  'rate-card',
                  `/kols/${encodeURIComponent(selectedKol.id)}/rate-cards.pdf`,
                  `rate-card-${slugifyName(selectedKol.name || selectedKol.id)}.pdf`
                )
              }
            >
              {activeExport === 'rate-card' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              Export PDF
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
